import React from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../app/store";
import {
  canUserSeeProduct,
  shouldHidePriceForProduct,
} from "../../../components/Catalogue/userCatalogUtils";
import t from "../../../components/I18N/TranslateHelpers";

interface ProductItem {
  id: number;
  price?: number;
  originalPrice?: number;
  discountedUnitPrice?: number;
  originalUnitPrice?: number;
  commercialUnit?: number;
  quantity?: number;
}

interface ProductPriceProps {
  productItem: ProductItem;
  mode?: "detail" | "catalog" | "cart" | "wishlist";
  quantity?: number;
  showHTVA?: boolean;
  className?: string;
}

const ProductPrice: React.FC<ProductPriceProps> = ({
                                                     productItem,
                                                     mode = "catalog",
                                                     quantity = 1,
                                                     showHTVA = true,
                                                     className = "",
                                                   }) => {
  const isAuthenticated = useSelector(
    (state: RootState) => state.userRdc.isAuthenticated
  );
  const userCatalogs = useSelector(
    (state: RootState) => state.userRdc.userCatalog
  );

  const productId = productItem?.id;
  const canSeeProduct = canUserSeeProduct(productId, userCatalogs);
  const shouldHidePrice = shouldHidePriceForProduct(productId, userCatalogs);

  const canSeePrice = isAuthenticated && canSeeProduct && !shouldHidePrice;

  if (!canSeePrice) return null;

  const formatPrice = (value?: number) =>
    value != null ? `${(value / 100).toFixed(2)} €` : null;

  // Mode panier (cart)
  if (mode === "cart") {
    const totalOriginal = (productItem.originalUnitPrice || 0) * quantity;
    const totalDiscounted = (productItem.discountedUnitPrice || 0) * quantity;

    return (
      <div className={`price-wrap lh-sm ${className}`}>
        {productItem.discountedUnitPrice === productItem.originalUnitPrice ? (
          <>
            <span className="price h6">
              {formatPrice(totalOriginal)}
              {showHTVA && totalOriginal > 0 && " HTVA"}
            </span>
            <br />
            <small className="text-muted">
              {formatPrice(productItem.originalUnitPrice)} / article
            </small>
          </>
        ) : (
          <>
            <span
              className="price h6 text-danger"
              style={{ textDecoration: "line-through" }}
            >
              {formatPrice(totalOriginal)}
            </span>{" "}
            <span className="price h6">
              {formatPrice(totalDiscounted)}
              {showHTVA && totalDiscounted > 0 && " HTVA"}
            </span>
            <br />
            <small
              className="text-muted text-danger"
              style={{ textDecoration: "line-through" }}
            >
              {formatPrice(productItem.originalUnitPrice)}
            </small>{" "}
            <small className="text-muted">
              {formatPrice(productItem.discountedUnitPrice)} / article
            </small>
          </>
        )}
      </div>
    );
  }

  // Mode standard (catalogue, détail, wishlist)
  const displayPrice =
    productItem.price ?? productItem.discountedUnitPrice ?? 0;
  const original =
    productItem.originalPrice ?? productItem.originalUnitPrice;

  return (
    <div className={`price-wrap ${className}`}>
      {mode === "detail" && (
        <small className="fst-italic fs-10 d-flex">
          {t("indicatif_price")}
        </small>
      )}
      {original && original !== displayPrice ? (
        <>
          <del className="price-old text-warning-light">
            {formatPrice(original)}
          </del>{" "}
          <strong className="price text-warning ml-2">
            {formatPrice(displayPrice)}
          </strong>
        </>
      ) : (
        <strong className="price">{formatPrice(displayPrice)}</strong>
      )}

      {productItem.commercialUnit && productItem.commercialUnit > 1 && (
        <small> /{productItem.commercialUnit}</small>
      )}

      {showHTVA && displayPrice > 0 && <span> {t("htva")}</span>}
    </div>
  );
};

export default ProductPrice;
